import { fallbackCloudflareOverview, fallbackDashboard, fallbackGrowthOverview } from "./mockData";
import type {
  CloudflareOverview,
  DashboardData,
  GrowthAction,
  GrowthOverview,
  HotGuidesAutomationOverview,
  HotGuidesStatus,
  LeadInput,
  Opportunity,
  PublicConfig,
  Site,
  SiteEventInput,
  Task,
  VideoFactoryInput,
  VideoGrowthOverview,
  VideoLeadInput,
  VideoProject
} from "./types";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {})
    },
    ...init
  });

  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<T>;
}

export async function loadDashboard(): Promise<DashboardData> {
  try {
    return await request<DashboardData>("/api/dashboard");
  } catch {
    return fallbackDashboard;
  }
}

export async function createOpportunity(payload: Partial<Opportunity>): Promise<Opportunity> {
  return request<Opportunity>("/api/opportunities", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function createSite(payload: Partial<Site>): Promise<Site> {
  return request<Site>("/api/sites", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function createTask(payload: Partial<Task>): Promise<Task> {
  return request<Task>("/api/tasks", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function createLead(
  payload: LeadInput
): Promise<{ ok: true; id: string; turnstile_status: string }> {
  return request<{ ok: true; id: string; turnstile_status: string }>("/api/leads", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function loadGrowthOverview(): Promise<GrowthOverview> {
  try {
    return await request<GrowthOverview>("/api/growth");
  } catch {
    return fallbackGrowthOverview;
  }
}

export async function createGrowthAction(payload: Partial<GrowthAction>): Promise<GrowthAction> {
  return request<GrowthAction>("/api/growth", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function loadHotGuidesStatus(): Promise<HotGuidesStatus> {
  return request<HotGuidesStatus>("/api/hot-guides-status");
}

export async function loadHotGuidesAutomation(): Promise<HotGuidesAutomationOverview> {
  return request<HotGuidesAutomationOverview>("/api/hot-guides");
}

export async function loadPublicConfig(): Promise<PublicConfig> {
  return request<PublicConfig>("/api/public-config");
}

export async function trackSiteEvent(payload: SiteEventInput): Promise<{ ok: true; id: string }> {
  return request<{ ok: true; id: string }>("/api/events", {
    body: JSON.stringify(payload),
    method: "POST"
  });
}

export async function createVideoLead(
  payload: VideoLeadInput
): Promise<{ ok: true; id: string; turnstile_status: string }> {
  return request<{ ok: true; id: string; turnstile_status: string }>("/api/video-leads", {
    body: JSON.stringify(payload),
    method: "POST"
  });
}

export async function loadVideoGrowth(): Promise<VideoGrowthOverview> {
  return request<VideoGrowthOverview>("/api/video-growth");
}

export async function loadCloudflareOverview(): Promise<CloudflareOverview> {
  try {
    return await request<CloudflareOverview>("/api/cloudflare");
  } catch {
    return fallbackCloudflareOverview;
  }
}

export async function runCloudflareAutomation(action: string): Promise<{ ok: true; message: string }> {
  return request<{ ok: true; message: string }>("/api/cloudflare", {
    method: "POST",
    body: JSON.stringify({ action })
  });
}

export async function generateVideoProject(
  payload: VideoFactoryInput
): Promise<{ ok: true; project: VideoProject }> {
  return request<{ ok: true; project: VideoProject }>("/api/video-factory", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}
